import { Component, OnInit, inject } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { GridModule } from '@progress/kendo-angular-grid';
import { EMPTY, Observable, take, tap } from 'rxjs';

import {
  ReferenceEntityType,
  ReferenceTranslationEntry,
  ReferenceTranslationSet,
  ReferenceTranslationsView,
} from 'src/app/common/ReferenceTranslations';
import { CallApiService } from 'src/app/services/api-action.service';
import { ReferenceTranslationService } from 'src/app/services/reference-translation.service';
import { AsyncClickDirective } from 'src/app/directives/async-click.directive';

/** A row as the grid renders it: the stored entry plus the value currently in its input. */
interface TranslationRow extends ReferenceTranslationEntry {
  entityType: ReferenceEntityType;
  /** What the input holds right now — compared against `translations` to decide "changed". */
  edited: Record<string, string>;
}

/**
 * #178 slice F / backend #701: the Carrier's page for the display names of the curated reference
 * sets — product categories, customer groups and payment terms.
 *
 * **One generic page rather than a tab per entity** (#178 decision 5). Not only because the
 * backend answers all three sets in one call, but because none of the three entities has an edit
 * page to hang a tab on: categories are create-only, and there is no customer-group or
 * payment-term screen at all. A tab would have meant building three host pages first, for one
 * customer group and one payment term.
 *
 * **The source language is read-only**, taken from `sourceLanguage` in the response rather than
 * hardcoded as `de`. It is the entity's own text — the row the German translation is seeded from,
 * and for a category the input the resolver reads (#538) — so editing it here would break the
 * invariant that the two cannot drift. The backend refuses it with `languageNotEditable`; this
 * page simply does not offer it, and the day a third language arrives nothing here changes.
 *
 * **Saving is per row.** The `PUT` takes a partial map, so one row is one small write, an
 * unchanged value is a no-op, and a rejection lands on the row that caused it instead of leaving
 * a page half-saved.
 */
@Component({
  imports: [CommonModule, GridModule, AsyncClickDirective],
  selector: 'app-reference-translations',
  templateUrl: './reference-translations.component.html',
})
export class ReferenceTranslationsComponent implements OnInit {
  private referenceTranslationService = inject(ReferenceTranslationService);
  private apiActionService = inject(CallApiService);
  private location = inject(Location);

  public loading = true;
  public view: ReferenceTranslationsView | null = null;
  /** The grid rows per set, in the response's order. */
  public rowsByType: { entityType: ReferenceEntityType; rows: TranslationRow[] }[] = [];

  // The route is reachable by URL for anyone signed in, exactly like the other Carrier pages
  // (`resolution-vocabulary`, `email-queue`): there is no Carrier guard in this app, and the
  // real gate is the backend, which answers a non-Carrier with 403. That surfaces as the load
  // error below rather than as a half-rendered page — so the component needs no profile of its
  // own, and does not keep one.
  public ngOnInit(): void {
    this.load();
  }

  /** The languages that get an editable column, in the response's order. */
  public get editableLanguages(): string[] {
    return this.view?.editableLanguages ?? [];
  }

  public get sourceLanguage(): string {
    return this.view?.sourceLanguage ?? '';
  }

  /** The heading for a set — German prose is fine here, this page is Carrier-only. */
  public setTitle(entityType: ReferenceEntityType): string {
    switch (entityType) {
      case 'ProductCategory':
        return $localize`:@@referenceTranslations.set.productCategory:Warengruppen`;
      case 'CustomerGroup':
        return $localize`:@@referenceTranslations.set.customerGroup:Kundengruppen`;
      case 'PaymentTerm':
        return $localize`:@@referenceTranslations.set.paymentTerm:Zahlungsbedingungen`;
      default:
        // A set this build does not know yet: name it by its own type rather than dropping it.
        return entityType;
    }
  }

  public onInput(row: TranslationRow, language: string, value: string): void {
    row.edited = { ...row.edited, [language]: value };
  }

  /** True while the row's inputs differ from what is stored — what enables its save button. */
  public isChanged(row: TranslationRow): boolean {
    return this.editableLanguages.some(
      (language) => (row.edited[language] ?? '') !== (row.translations?.[language] ?? ''),
    );
  }

  /**
   * Saves one row. Returns a factory so the template stays a plain method call and the request
   * runs only on click (the `[asyncClick]` convention for a parameterised action).
   *
   * Only the changed languages are sent: the map is partial by contract, and a `PUT` that
   * restates unchanged values would make a no-op look like an edit in the revision log.
   */
  public onSave(row: TranslationRow): () => Observable<unknown> {
    return () => {
      const namesByLanguage: Record<string, string> = {};
      for (const language of this.editableLanguages) {
        const value = (row.edited[language] ?? '').trim();
        if (value !== (row.translations?.[language] ?? '')) {
          namesByLanguage[language] = value;
        }
      }
      if (!Object.keys(namesByLanguage).length) {
        return EMPTY;
      }

      return this.apiActionService
        .callWithToasts$(
          this.referenceTranslationService.update(row.entityType, row.id, { namesByLanguage }),
          $localize`:@@referenceTranslations.saved:Die Bezeichnung wurde gespeichert.`,
          $localize`:@@referenceTranslations.saveError:Die Bezeichnung konnte nicht gespeichert werden.`,
        )
        .pipe(
          tap(() => {
            // Adopt what was just written, so the row stops reading as changed without a re-read
            // of all three sets. A rejection leaves the input as typed, which is what lets the
            // user correct it.
            row.translations = { ...row.translations, ...namesByLanguage };
            row.edited = { ...row.translations };
          }),
        );
    };
  }

  public onReset(row: TranslationRow): void {
    row.edited = { ...row.translations };
  }

  public onBackClicked(): void {
    this.location.back();
  }

  private load(): void {
    this.loading = true;
    this.referenceTranslationService
      .get()
      .pipe(take(1))
      .subscribe({
        next: (view) => {
          this.view = view;
          this.rowsByType = (view?.sets ?? []).map((set) => ({
            entityType: set.entityType,
            rows: this.toRows(set),
          }));
          this.loading = false;
        },
        error: () => {
          this.view = null;
          this.rowsByType = [];
          this.loading = false;
        },
      });
  }

  private toRows(set: ReferenceTranslationSet): TranslationRow[] {
    return (set.entries ?? []).map((entry) => ({
      ...entry,
      entityType: set.entityType,
      translations: entry.translations ?? {},
      edited: { ...(entry.translations ?? {}) },
    }));
  }
}
